// Terms & Conditions — generic UK B2B SaaS template with extended EHE
// disclaimer / limitation of liability sections.
// AI-drafted; legal review recommended before launch. Suitable as baseline.

import { Link } from "react-router-dom";
import LegalShell from "../components/LegalShell";

const LAST_UPDATED = "2 June 2026";

export default function Terms() {
  return (
    <LegalShell title="Terms &amp; Conditions" lastUpdated={LAST_UPDATED}>
      <p>
        These Terms &amp; Conditions ("Terms") govern your use of Troughmate
        (the "Service") operated by What Does The Horse Drink Ltd, a company
        registered in England and Wales ("we", "us", "our"). By creating an
        account or using the Service you agree to these Terms.
      </p>

      <h2>1. The Service</h2>
      <p>
        Troughmate is a hydration tracking tool for equine yards. It lets you
        log daily water intake, exercise, feed, droppings, and behaviour for
        each horse on your yard, and provides a hydration status estimate
        based on the Equine Hydration Estimator (the "EHE algorithm").
      </p>

      <h2>2. Your account</h2>
      <p>
        You are responsible for maintaining the confidentiality of your
        account credentials and for all activity that occurs under your
        account. You must notify us immediately if you suspect any
        unauthorised access. You agree to provide accurate information and
        keep it up to date.
      </p>

      <h2>3. Important: the EHE algorithm is a guide, not veterinary advice</h2>
      <p>
        <strong>
          The Equine Hydration Estimator (EHE) provides a research-backed
          estimate of a horse's hydration status. It is a decision-support
          tool only and is not a substitute for professional veterinary
          examination, diagnosis, or treatment.
        </strong>
      </p>
      <p>
        The EHE algorithm is derived from research conducted at the
        University of Exeter. While the underlying theory is sound and the
        algorithm has been validated against published estimates of equine
        water requirements, the output
        is an approximation and may not reflect the actual hydration needs
        of an individual horse. Factors including but not limited to age,
        breed, health, illness, medication, stress, climate, and exercise
        intensity can affect actual water requirements in ways the algorithm
        does not fully account for.
      </p>
      <p>
        <strong>You agree that:</strong>
      </p>
      <ul>
        <li>
          You will not use the Service as your sole source of decision-making
          regarding the health, welfare, or hydration of any horse.
        </li>
        <li>
          You will consult a qualified veterinary surgeon for any concerns
          about a horse's health, including but not limited to suspected
          dehydration, colic, or any other condition.
        </li>
        <li>
          The Service is provided for informational purposes only and does
          not constitute veterinary advice.
        </li>
        <li>
          We are not liable for any decision you make, or fail to make, on
          the basis of information provided by the Service, including any
          consequence to the health or welfare of a horse.
        </li>
      </ul>

      <h2>4. Acceptable use</h2>
      <p>
        You agree not to:
      </p>
      <ul>
        <li>Use the Service for any unlawful purpose.</li>
        <li>Attempt to gain unauthorised access to any part of the Service.</li>
        <li>Reverse engineer, decompile, or copy the Service.</li>
        <li>Use the Service to send spam, harass others, or distribute malware.</li>
        <li>Resell, sublicense, or commercialise the Service without our consent.</li>
      </ul>

      <h2>5. Your data</h2>
      <p>
        Information you enter (yard details, horse profiles, daily logs) belongs
        to you. We process it as described in our{" "}
        <Link to="/privacy">Privacy Policy</Link>. You grant us a limited
        licence to host, store, and display that data so we can provide the
        Service to you.
      </p>

      <h2>6. Intellectual property</h2>
      <p>
        The Service, including all software, design, content, and trademarks,
        is owned by What Does The Horse Drink Ltd or its licensors. The EHE
        algorithm and underlying research are documented in their original
        publication; nothing in these Terms transfers any intellectual
        property rights to you beyond the limited right to use the Service.
      </p>

      <h2>7. Fees</h2>
      <p>
        The app is currently provided free of charge during our beta period.
        We reserve the right to introduce pricing in the future, with at
        least 30 days' notice to existing users. The Troughmate sensor (when
        released) will be a paid product with separately disclosed pricing.
      </p>

      <h2>8. Termination</h2>
      <p>
        You may close your account at any time from the Settings page. We
        may suspend or terminate your access if you breach these Terms or
        use the Service in a way that risks harm to yourself, other users,
        or our infrastructure. On termination, your data will be deleted as
        described in our Privacy Policy.
      </p>

      <h2>9. Disclaimer of warranties</h2>
      <p>
        The Service is provided "as is" and "as available" without warranties
        of any kind, whether express or implied, including but not limited
        to warranties of merchantability, fitness for a particular purpose,
        non-infringement, accuracy, or availability. We do not warrant that
        the Service will be uninterrupted, secure, or error-free, or that
        defects will be corrected.
      </p>

      <h2>10. Limitation of liability</h2>
      <p>
        To the maximum extent permitted by law:
      </p>
      <ul>
        <li>
          We are not liable for any indirect, incidental, special,
          consequential, or punitive damages, or any loss of profits or
          revenues, whether incurred directly or indirectly.
        </li>
        <li>
          We are not liable for any harm, injury, illness, or death to any
          horse or other animal arising from your use of the Service.
        </li>
        <li>
          Our total aggregate liability for any claim arising out of or
          relating to the Service shall not exceed the greater of (a) the
          amount you paid us in the 12 months before the claim arose, or
          (b) £100.
        </li>
      </ul>
      <p>
        Nothing in these Terms excludes or limits our liability for death or
        personal injury caused by our negligence, for fraud, or for any
        other liability which cannot be excluded under English law.
      </p>

      <h2>11. Indemnity</h2>
      <p>
        You agree to indemnify and hold us harmless from any claims arising
        from your use of the Service, your breach of these Terms, or your
        violation of any third-party rights.
      </p>

      <h2>12. Changes to these Terms</h2>
      <p>
        We may update these Terms from time to time. Material changes will
        be notified by email or via a notice in the app. Continued use of
        the Service after changes take effect constitutes acceptance.
      </p>

      <h2>13. Governing law</h2>
      <p>
        These Terms are governed by the laws of England and Wales. Any
        dispute shall be subject to the exclusive jurisdiction of the
        English courts.
      </p>

      <h2>14. Contact</h2>
      <p>
        Questions about these Terms:{" "}
        <a href="mailto:hello@troughmate.com">hello@troughmate.com</a>.
      </p>

      <p style={{ marginTop: 40, fontSize: 13, color: "var(--ink-3)" }}>
        See also our <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </LegalShell>
  );
}
